// Shared constants + pure Fine-Gold-Equivalent math helpers for the Job Work module.
export const KARATS = ["24K", "22K", "18K", "14K", "9K"];
export const PURITY_OPTIONS = [...KARATS, "Other"];
export const STATUS_LABEL = { draft: "Draft", issued: "Issued", in_progress: "In Progress", received: "Received", qc: "QC", completed: "Completed", cancelled: "Cancelled" };
export const STATUS_STYLE = {
  draft: "bg-[#F0E9E9] text-[#5A4A4A] border border-[#DDD0D0]",
  issued: "bg-[#EAF1F7] text-[#1E3E5C] border border-[#C7D7E6]",
  in_progress: "bg-[#FBF1DF] text-[#7A4B12] border border-[#E7D0A8]",
  received: "bg-[#F3ECFB] text-[#4B2E7A] border border-[#D8C7EE]",
  qc: "bg-[#F3ECFB] text-[#4B2E7A] border border-[#D8C7EE]",
  completed: "bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]",
  cancelled: "bg-[#F0E9E9] text-[#5A4A4A] border border-[#DDD0D0]",
};
export const CLASSIFICATION_LABEL = { approved_wastage: "Approved Wastage", recoverable_wastage: "Recoverable Wastage", shortage: "Shortage", adjustment: "Adjustment" };

export const emptyForm = () => ({
  karigar_id: "", job_date: new Date().toISOString().slice(0, 10), customer_ref: "", product_ref: "", linked_product_id: "",
  quantity: 1, gold: { purity: "22K", purity_fine_pct: "", gross_weight_g: "" },
  diamonds: [], stones: [], other_materials: [],
  expected_finished_weight_g: "", expected_wastage_g: "", making_charges: "", expected_completion_date: "", notes: "",
});
export const purityMap = { "24K": 1, "22K": 0.916, "18K": 0.75, "14K": 0.585, "9K": 0.375 };
export const CARAT_TO_GRAM = 0.2; // 1 carat = 0.2 grams — same conversion used across Product pricing & Purchases

// Fine Gold Equivalent (FGE) — the single master formula used everywhere in this page:
// Fine Gold = Net Metal Weight x Purity ÷ 1000. "Other" purity takes an explicit per-mille fineness
// (e.g. 999, 995) instead of a karat label. 999 = 99.9% fine, 995 = 99.5%, 916 = 91.6% (22K),
// 750 = 75% (18K), 585 = 58.5% (14K), 375 = 37.5% (9K).
export function purityFactor(purity, customFinePct) {
  if (purity === "Other") return (Number(customFinePct) || 0) / 1000;
  return purityMap[purity] ?? 1;
}
export function stoneCaratTotal(diamonds, stones) {
  const d = (diamonds || []).reduce((s, x) => s + (Number(x.carat) || 0), 0);
  const s = (stones || []).reduce((s2, x) => s2 + (Number(x.carat) || 0), 0);
  return d + s;
}
// Gross Weight − Stone Weight = Net Metal; Net Metal x Purity = Fine Gold. Never mixes the four.
export function goldBreakdown(gross, purity, customFinePct, stoneCarat) {
  const g = Number(gross) || 0;
  const stoneWeightG = (Number(stoneCarat) || 0) * CARAT_TO_GRAM;
  const netMetalG = Math.max(0, g - stoneWeightG);
  const fineG = netMetalG * purityFactor(purity, customFinePct);
  return { grossG: g, stoneWeightG, netMetalG, fineG };
}

// Received Purity is entered directly in per-mille (999 = 99.9% fine, 995 = 99.5%, 916 = 91.6% ≈ 22K,
// 750 = 75% ≈ 18K, 585 = 58.5% ≈ 14K, 375 = 37.5% ≈ 9K) — the standard way melted/scrap/returned gold
// is graded, rather than a karat label.
export const FINE_PURITY_PRESETS = [
  { value: "999", label: "999 (99.9% fine)" },
  { value: "995", label: "995 (99.5%)" },
  { value: "916", label: "916 (91.6% · 22K)" },
  { value: "750", label: "750 (75% · 18K)" },
  { value: "585", label: "585 (58.5% · 14K)" },
  { value: "375", label: "375 (37.5% · 9K)" },
  { value: "custom", label: "Custom" },
];
export const KARAT_TO_PERMILLE = { "24K": "999", "22K": "916", "18K": "750", "14K": "585", "9K": "375" };

// Receive form: Stone Weight is weighed directly in grams (not derived from carats).
// Finished Net Metal = Gross − Stone; Fine Gold Received = Net Metal x Purity ÷ 1000.
export function receiveFineBreakdown(gross, stoneWeightG, finePct) {
  const g = Number(gross) || 0;
  const s = Number(stoneWeightG) || 0;
  const netMetalG = Math.max(0, g - s);
  const fineG = netMetalG * ((Number(finePct) || 0) / 1000);
  return { netMetalG, fineG };
}
